import React, { useState, useEffect } from "react";
import { getFirestore, collection, query, orderBy, limit, getDocs, doc, getDoc, where, collectionGroup } from "firebase/firestore";
import TopMenuBar from "../layouts/TopMenuBar";
import ChartTitleHeader from "../assets/nuclearFifty.png"; 
import ChartTitleHeaderDark from "../assets/nuclearFifty_darkmode.png";
import { useMusic } from '../context/MusicContext';
import { useProfile } from '../context/ProfileContext';
import AddToPlaylistModal from '../components/music/AddToPlaylistModal';
import CreatePlaylistModal from '../components/music/CreatePlaylistModal';
import ComingSoonModal from '../components/miscellaneous/ComingSoonModal';
import FanLeaderboard from '../components/fans/FanLeaderboard'; 
import PlayDark from "../assets/play_darkmode.png"; 
import PauseDark from "../assets/pause_darkmode.png";
import PlayStandard from "../assets/play.png";
import saveIcon from "../assets/save.png";
import saveIconWhite from "../assets/trends_save.png";
import fanActiveIcon from '../assets/fan_active.png';
import toast from 'react-hot-toast';

const ChartsPage = () => {
  const { isDarkMode, activeProfile } = useProfile();
  const [selectedSong, setSelectedSong] = useState(null);
  const [isAddToPlaylistOpen, setIsAddToPlaylistOpen] = useState(false);
  const [isCreatePlaylistOpen, setIsCreatePlaylistOpen] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [weekKey, setWeekKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [isComingSoonModalOpen, setIsComingSoonModalOpen] = useState(false);
  
  // Leaderboard Modal Tracking States
  const [leaderboardArtistId, setLeaderboardArtistId] = useState(null);
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);
  const [topFansCache, setTopFansCache] = useState({}); 

  const db = getFirestore();
  const { setQueueAndPlay, currentSong, isPlaying, togglePlayPause } = useMusic();

  const handleOpenPlaylistModal = (song) => {
    if (activeProfile?.type === 'artist') {
      toast.error("Artist accounts cannot add songs to playlists.");
      return;
    }
    setSelectedSong(song);
    setIsAddToPlaylistOpen(true);
  };

  const handleNavigateToCreate = () => {
    setIsAddToPlaylistOpen(false);
    setIsCreatePlaylistOpen(true);
  };

  const handleOpenLeaderboard = (artistId) => {
    if (!artistId) {
      toast.error("Leaderboard metrics unavailable for this creator.");
      return;
    }
    setLeaderboardArtistId(artistId);
    setIsLeaderboardOpen(true);
  };

  useEffect(() => {
    const fetchLatestChart = async () => {
      try {
        setLoading(true);

        // 🌟 CHANGED: Synchronize cache namespaces to use top_50 identifiers
        const cachedChart = sessionStorage.getItem('chart_top_50');
        const cachedDate = sessionStorage.getItem('chart_date');
        
        if (cachedChart && cachedDate) {
          const parsedSongs = JSON.parse(cachedChart);
          setChartData(parsedSongs);
          setWeekKey(cachedDate);
          setLoading(false);
          fetchTopFansForChart(parsedSongs); 
          return;
        }

        const chartsRef = collection(db, 'weeklyCharts');
        const latestQuery = query(
          chartsRef, 
          where("status", "==", "official"), 
          orderBy("chartDate", "desc"), 
          limit(1)
        );
        
        const chartSnap = await getDocs(latestQuery);
        if (chartSnap.empty) {
          setLoading(false);
          return;
        }

        const latestDate = chartSnap.docs[0].id;
        setWeekKey(latestDate);

        // 🌟 CHANGED: Point fetch path node directly to topFifty document 
        const displayRef = doc(db, 'weeklyCharts', latestDate, 'displays', 'topFifty');
        const displaySnap = await getDoc(displayRef);

        if (displaySnap.exists()) {
          const songs = displaySnap.data().songs;
          setChartData(songs);

          // Save tracking values
          sessionStorage.setItem('chart_top_50', JSON.stringify(songs));
          sessionStorage.setItem('chart_date', latestDate);
          fetchTopFansForChart(songs); 
        }
        
      } catch (error) {
        console.error("Error fetching chart:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestChart();
  }, [db]);

  const fetchTopFansForChart = async (songs) => {
    const newCache = { ...topFansCache }; // Holds top fan avatar references
    const updatedChartData = [...songs];
    let dataChanged = false;

    try {
      for (let i = 0; i < updatedChartData.length; i++) {
        const song = updatedChartData[i];
        if (!song.artistId) continue;

        // TASK A: Pull the live, current Artist photoURL if not already fetched in this pass
        if (!song.artistPhotoURL) {
          const artistProfileSnap = await getDoc(doc(db, 'artist_accounts', song.artistId));
          if (artistProfileSnap.exists()) {
            // Check both standard field naming variations in your database schema
            updatedChartData[i].artistPhotoURL = 
              artistProfileSnap.data().photoURL || 
              artistProfileSnap.data().avatarURL || 
              artistProfileSnap.data().artistAvatar || "";
            dataChanged = true;
          }
        }

        // TASK B: Pull the #1 Fan Profile picture for the preview node
        /*
        if (!newCache[song.artistId]) {
          const topFanQuery = query(
            collectionGroup(db, 'artistsTipped'),
            where('artistId', '==', song.artistId),
            orderBy('overallFanScore', 'desc'),
            limit(1)
          );

          const querySnap = await getDocs(topFanQuery);
          
          if (!querySnap.empty) {
            const topFanDoc = querySnap.docs[0];
            const userDocRef = topFanDoc.ref.parent.parent;
            const userId = userDocRef?.id;

            if (userId) {
              const userProfileSnap = await getDoc(doc(db, 'users', userId));
              if (userProfileSnap.exists()) {
                newCache[song.artistId] = userProfileSnap.data().photoURL || userProfileSnap.data().avatarURL || "fallback";
              }
            }
          }
        }
        */
      }

      // Only trigger React re-renders if data was actually found and modified
      if (dataChanged) {
        setChartData(updatedChartData);
      }
      setTopFansCache(newCache);
    } catch (err) {
      console.warn("[TopFans/Artist LazyLoader] Resolutions bounded: ", err);
    }
  };

  const handlePlay = (song) => {
    if (currentSong?.id === song.id) {
      togglePlayPause();
    } else {
      const clickedIndex = chartData.findIndex((item) => item.id === song.id);
      const targetIndex = clickedIndex !== -1 ? clickedIndex : 0;
      setQueueAndPlay(chartData, targetIndex, 'Top Fifty');
    }
  };

  const renderTrend = (song) => {
    const lastPos = song.lastRank || song.lastPosition;
    const currentPos = song.position;
    if (!lastPos) return <span className="text-[10px] font-bold text-blue-500 uppercase">NEW</span>;
    const diff = lastPos - currentPos;
    if (diff > 0) return <span className="text-emerald-500">▲ {diff}</span>;
    if (diff < 0) return <span className="text-rose-500">▼ {Math.abs(diff)}</span>;
    return <span className="text-gray-400">—</span>;
  };

  return (
    <div className={`min-h-screen flex flex-col font-sans transition-colors duration-500 ${
      isDarkMode ? "bg-black" : "bg-white"
    }`}>
      <TopMenuBar />

      {/* HEADER SECTION */}
      <div className={`w-full flex flex-col items-center pt-8 pb-4 px-4 transition-all ${
        isDarkMode 
          ? "bg-gradient-to-b from-brand-gray/20 to-black" 
          : "bg-gradient-to-b from-slate-50 to-white"
      }`}>
        <img 
          src={isDarkMode ? ChartTitleHeaderDark : ChartTitleHeader} 
          alt="Weekly Top 20" 
          className="max-w-md w-full mb-2" 
        />
        <p className="text-slate-500 dark:text-white/80 font-bold uppercase tracking-widest text-xs">
          Week of {weekKey}
        </p>
        
        <button 
          onClick={() => setIsComingSoonModalOpen(true)} 
          className="mt-6 px-8 py-3 rounded-full font-black text-sm uppercase tracking-tighter hover:scale-105 transition-all shadow-xl bg-black dark:bg-[#ff00cc] text-white">
          Play Live Recording
        </button>
      </div>

      {/* TABLE SECTION */}
      <div className="flex-grow max-w-5xl w-full mx-auto p-4 lg:p-8">
        {loading ? (
          <div className="flex justify-center items-center h-64 text-slate-300 dark:text-brand-gray animate-pulse font-black uppercase tracking-widest text-xs">
            Loading the Hits...
          </div>
        ) : (
          <table className="w-full border-separate border-spacing-y-3">
            <thead>
              <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-4">
                <th className="pb-4 text-center w-12">#</th>
                <th className="pb-4 text-left">Song</th>
                <th className="pb-4 text-center">Trend</th>
                <th className="pb-4 text-center">Play</th>
                <th className="pb-4 text-center">Add</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((song) => {
                const isThisSongActive = currentSong?.id === song.id;
                const topFanAvatarUrl = topFansCache[song.artistId];

                return (
                  <tr 
                    key={song.id} 
                    className={`group transition-all rounded-xl shadow-sm ${
                      isDarkMode 
                        ? "bg-brand-gray/50 hover:bg-brand-gray/20 border border-white/5" 
                        : "bg-white hover:bg-slate-50 border border-slate-100"
                    }`}
                  >
                    {/* Position */}
                    <td className={`py-4 text-center font-black italic text-xl ${
                      isDarkMode ? "text-white" : "text-slate-900"
                    }`}>
                      {song.position}
                    </td>

                    {/* Artwork & Info */}
                    <td className="py-4 flex items-center gap-4">
                      <img 
                        src={song.albumArtworkURL || "https://via.placeholder.com/150"} 
                        alt="Album Art" 
                        className="w-14 h-14 rounded-lg shadow-inner object-cover"
                      />
                      <div className="flex flex-col">
                        <div className={`font-black leading-none mb-1 text-sm md:text-base ${
                          isDarkMode ? "text-white" : "text-slate-900"
                        }`}>
                          {song.artistName}
                        </div>
                        {/* Song Title + Appended GeoTag Inline */}
                        <div className="text-xs md:text-sm text-slate-500 dark:text-brand-white/50 font-bold italic mb-2 flex items-center gap-1.5 flex-wrap">
                          <span>{song.title}</span>
                          {(song.artistCity || song.artistState) && (
                            <span className="text-[10px] font-bold tracking-wider text-slate-400 not-italic">
                            {song.artistCity}{song.artistState ? `, ${song.artistState}` : ""}
                            </span>
                          )}
                        </div>

                        {/* 🌟 NEW INLINE TRIGGER: See Top Fans Hyperlink */}
                        <button
                          onClick={() => handleOpenLeaderboard(song.artistId)}
                          className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-left text-[#ff00cc] hover:text-[#d600a3] active:scale-95 transition-all w-fit group"
                        >
                          {/* Custom Active Fan Asset Graphic */}
                          <img 
                            src={fanActiveIcon} 
                            alt="SupaFans Active" 
                            className="w-4 h-4 object-contain transition-transform duration-150"
                          />
                          
                          <span className="underline underline-offset-4">SupaFans</span>
                          
                          {/* #1 Fan Profile Picture Peek Bubble (Pre-compiled Server Property) */}
                          {/*
                          {song.topFanPhotoURL && (
                            <img 
                              src={song.topFanPhotoURL} 
                              alt="Top Fan Profile" 
                              className="w-4 h-4 rounded-full object-cover border border-pink-200 dark:border-pink-800/40 ml-0.5 animate-in fade-in duration-300"
                            />
                          )}
                            */}
                        </button>
                      </div>
                    </td>

                    {/* Trend */}
                    <td className="py-4 text-center font-bold text-sm">
                      {renderTrend(song)}
                    </td>

                    {/* Play Button */}
                    <td className="py-4 text-center">
                      <div className="flex items-center justify-center">
                        <button 
                          onClick={() => handlePlay(song)}
                          className="w-12 h-12 flex items-center justify-center transition-transform active:scale-90 group"
                        >
                          {currentSong?.id === song.id ? (
                            <img 
                              src={isThisSongActive 
                                ? (isPlaying ? PauseDark : PlayDark) 
                                : (isDarkMode ? PlayDark : PlayStandard)
                              } 
                              className={`w-8 h-8 object-contain transition-opacity ${
                                !isThisSongActive && "opacity-60 group-hover:opacity-100"
                              }`}
                              alt="Play"
                            />
                          ) : (
                            <img 
                              src={PlayStandard} 
                              alt="Play" 
                              className="w-8 h-8 object-contain opacity-60 group-hover:opacity-100 transition-opacity" 
                            />
                          )}
                        </button>
                      </div>
                    </td>

                    {/* Add to Playlist */}
                    <td className="py-4 text-center">
                      <button 
                        onClick={() => handleOpenPlaylistModal(song)}
                        className="w-10 h-10 flex items-center justify-center mx-auto hover:scale-110 transition-transform active:scale-95 group"
                      >
                        <img 
                            src={isDarkMode ? saveIconWhite : saveIcon} 
                            className="w-7 h-7 object-contain opacity-70 group-hover:opacity-100" 
                            alt="Add" 
                          />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        
        <AddToPlaylistModal
          isOpen={isAddToPlaylistOpen}
          onClose={() => setIsAddToPlaylistOpen(false)}
          songToAdd={selectedSong}
          onNavigateToCreate={handleNavigateToCreate}
        />
        
        <CreatePlaylistModal
          isOpen={isCreatePlaylistOpen}
          onClose={() => setIsCreatePlaylistOpen(false)}
          songToAdd={selectedSong}
          source="fans_page_chart"
        />

        {/* SLIDE-IN MODAL OVERLAY */}
        {isLeaderboardOpen && (
          <div 
            onClick={() => setIsLeaderboardOpen(false)} // 🌟 Clicking backdrop closes modal
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 cursor-pointer"
          >
            {/* Inner Modal Content (e.stopPropagation prevents clicks inside the modal from closing it) */}
            <div 
              onClick={(e) => e.stopPropagation()} 
              className={`relative max-w-lg w-full rounded-3xl p-1 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 cursor-default ${
                isDarkMode ? "bg-slate-900" : "bg-white"
              }`}
            >
              <button
                onClick={() => setIsLeaderboardOpen(false)}
                className="absolute top-5 right-5 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors text-sm font-black text-slate-500 dark:text-slate-400"
              >
                ✕
              </button>
              <FanLeaderboard artistId={leaderboardArtistId} hideOwner={true} />
            </div>
          </div>
        )}

      </div>

      <ComingSoonModal 
        isOpen={isComingSoonModalOpen}
        onClose={() => setIsComingSoonModalOpen(false)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default ChartsPage;